<template>
  <v-dialog v-model="dialog" :max-width="width">
    <v-card class="rounded-lg pa-8" color="neutral" :loading="loading">
      <div class="d-flex align-start">
        <div class="d-flex flex-column fill-width">
          <div class="d-flex align-center">
            <img
              :src="this.user.photo"
              alt="Foto do Usuário"
              class="imgPerson rounded-lg mr-4"
            />

            <div>
              <div>{{ this.user.name }}</div>
              <div>{{ this.user.email }}</div>
            </div>
          </div>

          <section v-if="$can('attendances')">
            <v-divider class="my-2" />

            <div class="font-weight-bold text-body-2">
              Módulo de atendimento:
            </div>

            <div class="mb-2">{{ _attendanceRole }}</div>

            <div class="font-weight-bold text-body-2">
              {{ $t("profile.simultaneousAttendances") }}:
            </div>

            <div class="mb-2">{{ _attendanceLimitLabel }}</div>

            <AssociatedEntities module="attendances" @select="openEntity" />

            <v-switch
              v-if="_isAdmin || _isModerator || _isSupervisor"
              class="mt-0 pt-0"
              max-width="1rem"
              max-height="1rem"
              v-model="user.settings.attendancesNotification"
              @change="handleUpdateSettingsAttendanceNotifications"
              :label="`Permitir Notificações de Atendimentos`"
              hide-details
              :disabled="loading"
            >
            </v-switch>
          </section>

          <section v-if="$can('audiences')">
            <v-divider class="my-2" />

            <div class="font-weight-bold text-body-2">Módulo de audiência:</div>
            <div class="mb-2">{{ _audienceRole }}</div>

            <AssociatedEntities module="audiences" />
          </section>
        </div>
      </div>

      <v-row justify="center" class="mt-5">
        <v-btn
          class="text-none"
          color="primary"
          depressed
          dark
          @click="close()"
        >
          Fechar
        </v-btn>
      </v-row>
    </v-card>

    <JurisdictionDialog ref="jurisdictionDialog" />

    <CourtDialog ref="courtDialog" />

    <CounterDialog ref="counterDialog" />
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { getPermissionGroups } from "@/services/permissions";

import AssociatedEntities from "./partials/associatedEntities/AssociatedEntities.vue";
import JurisdictionDialog from "@/views/counters/children/jurisdictions/partials/jurisdictionDialog/JurisdictionDialog.vue";
import CourtDialog from "@/views/counters/children/courts/partials/courtDialog/CourtDialog.vue";
import CounterDialog from "@/views/counters/children/counters/partials/counterDialog/CounterDialog.vue";

export default {
  components: {
    AssociatedEntities,
    JurisdictionDialog,
    CourtDialog,
    CounterDialog,
  },

  name: "ProfileDialog",

  props: {
    width: {
      type: [String, Number],
      default: "600",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dialog: false,
      audienceGroups: [],
    };
  },

  computed: {
    ...mapState(["user"]),
    ...mapGetters(["attendanceRole"]),

    _isAdmin() {
      return this.attendanceRole === "admin";
    },

    _isModerator() {
      return this.attendanceRole === "moderator";
    },

    _isSupervisor() {
      return this.attendanceRole === "supervisor";
    },

    /**
     * Limite já resolvido pela API (pessoa > tribunal > default) e devolvido no
     * login em `effectiveMaxSimultaneousAttendances`. `null` = sem teto.
     */
    _attendanceLimitLabel() {
      const limit = this.user?.effectiveMaxSimultaneousAttendances;

      if (limit === null) return this.$t("profile.noAttendanceLimit");

      // Abaixo de 2 não há multiatendimento, e mostrar "1" sugeriria um limite
      // configurado quando na verdade o recurso está fora do ar para o usuário.
      return limit > 1
        ? String(limit)
        : this.$t("profile.multiAttendanceDisabled");
    },

    _attendanceRole() {
      const permissionGroup = this.user.permissionGroups.find(
        (el) => el.role === this.attendanceRole && el.module === "attendances"
      );

      return permissionGroup?.name || "";
    },

    _audienceRole() {
      const audienceEntities = this.user?.permissionGroups?.filter(
        (el) =>
          !!this.audienceGroups?.find(
            (perm) => perm._id === el._id && el.module === "audiences"
          )
      );

      return audienceEntities?.length ? audienceEntities[0].name : "--";
    },
  },

  beforeMount() {
    if (!this.user?.settings) {
      this.user.settings = {
        attendancesNotification: false,
      };
    }

    this.getAudiencePermissionGroups();
  },

  methods: {
    /**
     * Abre a entidade no modal já existente da tela de estrutura. Sem permissão
     * de edição o modal abre em modo de visualização — é o mesmo componente,
     * então não há caminho para editar sem permissão.
     */
    openEntity({ kind, entity }) {
      const targets = {
        group: { ref: "jurisdictionDialog", permission: "groups.edit" },
        subgroup: { ref: "courtDialog", permission: "subgroups.edit" },
        desk: { ref: "counterDialog", permission: "desks.edit" },
      };

      const target = targets[kind];
      if (!target) return;

      const mode = this.$can(target.permission) ? "edit" : "view";

      this.$refs[target.ref].openDialog(entity, mode);
    },

    open() {
      this.dialog = true;
    },

    close() {
      this.dialog = false;
    },

    handleUpdateSettingsAttendanceNotifications(value) {
      const { type, language, status } = this.user;

      const payload = {
        _id: this.user._id,
        ...(!!type && { type }),
        ...(!!language && { language }),
        ...(!!status && { status }),
        settings: {
          ...this.user.settings,
          attendancesNotification: value,
        },
      };
      this.$emit("update", payload);
    },

    async getAudiencePermissionGroups() {
      if (!this.$can("audiences")) return;

      try {
        const payload = {
          module: "audiences",
          type: "role",
        };

        const { data } = await getPermissionGroups(payload);

        this.audienceGroups = data;
      } catch (error) {
        this.handleAlert(error.data.message, "error");
      }
    },
  },
};
</script>

<style src="./style.scss" lang="scss" scoped />
